
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageSquare, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth/AuthContext';
import { formatDistanceToNow } from 'date-fns';

interface ModuleCommentsProps {
  moduleId: string;
}

interface Comment {
  id: string;
  comment: string;
  user_id: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  profile_photo_url?: string;
}

const ModuleComments: React.FC<ModuleCommentsProps> = ({ moduleId }) => {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Fetch module comments
  const { data: comments, isLoading } = useQuery({
    queryKey: ['module-comments', moduleId],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('course_build_comments')
          .select('id, comment, user_id, created_at')
          .eq('module_id', moduleId)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        // Fetch user details for each comment
        const commentsWithUsers = await Promise.all(
          (data || []).map(async (comment) => {
            const { data: userData, error: userError } = await supabase
              .from('user_profiles')
              .select('first_name, last_name, profile_photo_url')
              .eq('id', comment.user_id)
              .single();
            
            if (userError || !userData) {
              return {
                ...comment,
                first_name: 'Unknown',
                last_name: 'User',
                profile_photo_url: null
              };
            }
            
            return {
              ...comment,
              first_name: userData.first_name,
              last_name: userData.last_name,
              profile_photo_url: userData.profile_photo_url
            };
          })
        );
        
        return commentsWithUsers;
      } catch (error) {
        console.error('Error fetching module comments:', error);
        toast.error('Failed to load comments');
        return [];
      }
    }
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async (commentText: string) => {
      if (!user) throw new Error('User not logged in');
      setIsSubmitting(true);
      
      const { data, error } = await supabase
        .from('course_build_comments')
        .insert({
          module_id: moduleId,
          user_id: user.id,
          comment: commentText
        })
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setIsSubmitting(false);
      setNewComment('');
      queryClient.invalidateQueries({ queryKey: ['module-comments', moduleId] });
      toast.success('Comment added successfully');
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from('course_build_comments')
        .delete()
        .eq('id', commentId);
        
      if (error) throw error;
      return commentId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-comments', moduleId] });
      toast.success('Comment deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  });

  const handleSubmitComment = () => {
    if (!newComment.trim()) return;
    addCommentMutation.mutate(newComment.trim());
  };

  const handleDeleteComment = (commentId: string) => {
    if (confirm('Are you sure you want to delete this comment?')) {
      deleteCommentMutation.mutate(commentId);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center">
          <MessageSquare className="h-5 w-5 mr-2" />
          <span>Comments & Discussion</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex space-x-2">
          <Textarea
            placeholder="Add a comment or feedback..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="min-h-[80px] resize-y"
          />
          <Button 
            variant="outline" 
            size="icon"
            disabled={isSubmitting || !newComment.trim()}
            onClick={handleSubmitComment}
            className="self-end"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="flex items-start space-x-3">
                <div className="h-10 w-10 rounded-full bg-muted"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-muted rounded"></div>
                  <div className="h-10 bg-muted rounded"></div>
                </div>
              </div>
            </div>
          ) : comments && comments.length > 0 ? (
            comments.map((comment) => (
              <div key={comment.id} className="flex items-start space-x-3">
                <Avatar>
                  <AvatarImage src={comment.profile_photo_url || ''} alt={`${comment.first_name} ${comment.last_name}`} />
                  <AvatarFallback>{comment.first_name?.[0]}{comment.last_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">
                      {comment.first_name} {comment.last_name}
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                      </span>
                      {(isAdmin() || comment.user_id === user?.id) && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{comment.comment}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <MessageSquare className="h-8 w-8 mx-auto mb-2" />
              <p>No comments yet. Be the first to add a comment!</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ModuleComments;
